import React from 'react';
import { createRoot } from 'react-dom/client';
import App from './App';
import { captureAttribution } from './lib/attribution';
import { startFormTagging } from './lib/formTagging';
import './tailwind.css';

// Record where the visitor came from the moment the script loads — not when the
// chat opens. People usually browse several pages first, and by then the original
// referrer and campaign tags are long gone.
captureAttribution();

// Custom Web Component for embedding in websites
class ChatbotWidget extends HTMLElement {
    private botId: string | null = null;
    private root: ReturnType<typeof createRoot> | null = null;

    constructor() {
        super();

        // Attach Shadow DOM to isolate styles from the host page
        const shadow = this.attachShadow({ mode: 'open' });

        // Inject the widget stylesheet inside the shadow root
        const styleLink = document.createElement('link');
        styleLink.rel = 'stylesheet';
        // Use the hosted CSS that is produced by the build
        styleLink.href = 'https://chatbot.zentri.ai/chatbot-widget.css';
        shadow.appendChild(styleLink);

        // Add keyframes animation inline to ensure it's available
        const keyframesStyle = document.createElement('style');
        keyframesStyle.textContent = `
            @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
            }
        `;
        shadow.appendChild(keyframesStyle);

        // Create the container for the React app inside the shadow root
        const container = document.createElement('div');
        container.id = 'chatbot-widget-root';
        container.className = 'chatbot-widget-css-reset';
        shadow.appendChild(container);

        this.botId = this.getAttribute('data-bot-id');

        if (this.botId) {
            // Tag any third-party form already on this page (WordPress/Elementor/
            // Gravity Forms etc.) with a reference the CRM can redeem, the same
            // way the standalone snippet does for sites with no chatbot. Separate
            // from captureAttribution() above, which already ran unconditionally
            // at module load — this only needs bot_id, which isn't known until now.
            startFormTagging(this.botId);

            this.root = createRoot(container);
            this.root.render(
                <React.StrictMode>
                    <App botId={this.botId} />
                </React.StrictMode>
            );
        } else {

        }
    }

    disconnectedCallback() {
        if (this.root) {
            this.root.unmount();
            this.root = null;
        }
    }
}

if (!customElements.get('chatbot-widget')) {
    // Skip registration entirely when maintenance mode is enabled at build time.
    // The backend also returns 503 for API calls during maintenance.
    if (import.meta.env.VITE_MAINTENANCE_MODE === 'true') {
        console.log('[CHATBOT-WIDGET-V2] Maintenance mode is active — widget disabled.');
    } else {
        customElements.define('chatbot-widget', ChatbotWidget);
    }
}
export default ChatbotWidget;
